import React, { FC } from 'react';
import classnames from 'classnames';
import { history, useLocation } from 'umi';
// @ts-ignore
import * as rbd from 'react-beautiful-dnd';
import OverviewCard from './component/OverviewCard';
import ProjectRow from './component/ProjectRow';
import { TYPES } from '@sg/pages/StudioManagement/LiveTitles/pages/LiveVisualizationAll/const';
import { buildLiveTitlesUrl } from '@sg/pages/StudioManagement/LiveTitles/utils/liveTitlesUrl';
import './index.scss';
import CardCommon from '@sg/components/CardCommon';
import NoData from '@/pages/intelligence/components/NoData';
import Loading from '@intelligence/components/Loading';
import { LiveTitlesTab, createBtnPoint, SGBuriedBtnPoint } from '@sg/utils/buriedPoint';

interface IProps {
  selectedMonth?: any;
  cascadeValue?: string[];
  isEditing?: boolean;
  handleDateChange?: (value: any) => void;
  handleConfirm?: () => void;
  handleProjectsReorder?: (projects: any[]) => void;
  handleProjectsReorderCancel?: () => void;
  handleEditNotes?: () => void;
  setIsEditingNotes?: (value: boolean) => void;
  loading?: boolean;
  editable?: boolean;
  cascadeTreeOptions?: any[];
  allCascadeValues?: string[];
  cascadeInfoList?: any[];
  activeOverview?: string;
  handleActiveOverview?: (overview: string) => void;
  summaryDataMC?: any;
  summaryDataLO?: any;
  /** When exactly one studio is selected in the filter, its name is appended to the Live-Ops Overview card title. */
  singleSelectedStudioName?: string;
  /** Loading state while Live-Ops data is re-fetched after filter change. */
  loFilterLoading?: boolean;
  projectsMC?: any[];
  projectsLO?: any[];
  projects?: any[];
  /** GameSelectAll 加载到的 studio/game 列表，用于点击 project 图标时映射 unified_id */
  studioList?: any[];
}

const LiveTitlesOverview: FC<IProps> = (props: any) => {
  const {
    selectedMonth,
    cascadeValue,
    isEditing,
    handleDateChange,
    handleConfirm,
    handleProjectsReorder,
    handleProjectsReorderCancel,
    handleEditNotes,
    setIsEditingNotes,
    loading,
    editable = true,
    cascadeTreeOptions = [],
    allCascadeValues = [],
    cascadeInfoList = [],
    activeOverview,
    handleActiveOverview,
    summaryDataMC,
    summaryDataLO,
    projectsMC,
    projectsLO,
    projects,
    studioList,
    singleSelectedStudioName,
    loFilterLoading,
  } = props;

  const location = useLocation();

  // Which overview card is active: mc or live_ops

  // Filter + sort projects based on active overview card.
  // Rule 3.4: primary sort = gross_revenue desc; secondary = mgmtOrder asc.
  // const filteredProjects = useMemo(() => {
  //   // 先按类型过滤
  //   const filtered = activeOverview === 'mc' ? projectsMC : projectsLO;
  //   // 再按筛选器过滤
  //   return [...filtered]?.filter((project) => {
  //     return cascadeValue.includes(project.game_title);
  //   });
  // }, [activeOverview, cascadeValue, projectsMC, projectsLO]);

  // 与 GameSelectAll.handleImageClick 行为对齐：
  // 把 URL 上的 game_title 切换为目标 game/studio 对应的 unified_id，
  // 并 push 到 /strategic/home，触发父组件 RoutePrompt 拦截及 studio 状态同步。
  const handleProjectIconClick = (project: any) => {
    if (!project?.game_title) {
      return;
    }
    // GameSelectAll 的 options 由 LiveTitles/index.tsx 的 fetchProjectData 生成，
    // 每项包含 { name: game_title, unified_id, ... }，按 game_title 反查 unified_id
    const matched = (studioList || []).find(
      (item: any) => item?.name === project.game_title,
    );
    const targetUnifiedId = matched?.unified_id;
    if (!targetUnifiedId) {
      return;
    }

    // 仅在 LiveTitles 子页（subtitle=titles）下才更新 URL，避免误跳其他子页
    const params = new URLSearchParams(location.search);
    if (params.get('subtitle') !== 'titles') {
      return;
    }
    const btnPoint = createBtnPoint(
      LiveTitlesTab,
      'liveVisualizationAllProjectRedirectClick',
      `Project Redirect - ${project.game_title}`,
    );
    if (btnPoint) {
      SGBuriedBtnPoint(btnPoint);
    }
    history.push(
      buildLiveTitlesUrl({
        liveView: 'business',
        gameTitle: targetUnifiedId,
      }),
    );
  };

  // 拖拽排序: 完成后重排可视 projects, 由父级 hook 同步底层源并生成提交格式
  const handleProjectDragEnd = (result: any) => {
    if (!result?.destination) {
      return;
    }
    const sourceIndex = result?.source?.index;
    const destIndex = result?.destination?.index;
    if (sourceIndex === destIndex) {
      return;
    }
    const reordered = Array.from(projects || []);
    const [moved] = reordered.splice(sourceIndex, 1);
    reordered.splice(destIndex, 0, moved);
    handleProjectsReorder?.(reordered);
  };

  return (
    <>
      {/* Overview cards (Miniclip + Live-Ops) */}
      <div className="dbs-lto__overview dbs-home-studio-card">
        <OverviewCard
          key={TYPES.mc}
          type={TYPES.mc}
          data={summaryDataMC}
          active={activeOverview === TYPES.mc}
          onClick={() => {
            const btnPoint = createBtnPoint(
              LiveTitlesTab,
              'liveVisualizationAllOverviewCardClick',
              'Overview Card Click - Miniclip Overview',
            );
            if (btnPoint) {
              SGBuriedBtnPoint(btnPoint);
            }
            handleActiveOverview?.(TYPES.mc);
          }}
        />
        <OverviewCard
          key={TYPES.lo}
          type={TYPES.lo}
          data={summaryDataLO}
          active={activeOverview === TYPES.lo}
          singleSelectedStudioName={singleSelectedStudioName}
          loading={loFilterLoading}
          onClick={() => {
            const btnPoint = createBtnPoint(
              LiveTitlesTab,
              'liveVisualizationAllOverviewCardClick',
              'Overview Card Click - Live-Ops Overview',
            );
            if (btnPoint) {
              SGBuriedBtnPoint(btnPoint);
            }
            handleActiveOverview?.(TYPES.lo);
          }}
        />
      </div>
      {/* Arrow connector from active card to project list */}
      <CardCommon className={classnames('dbs-lto__project-list card-common-Inertia Inactive', {
        'dbs-lto__project-list--editing': isEditing,
        'dbs-lto__arrow-connector--left': activeOverview === TYPES.mc,
        'dbs-lto__arrow-connector--right': activeOverview === TYPES.lo,
      })}>
        {(projects?.length ?? 0) === 0 && !loFilterLoading ? (
          <NoData
            style={{ height: '300px' }}
            text={
              cascadeValue &&
              !cascadeValue.includes('all') &&
              cascadeValue.length > 0
                ? 'No data matches the current filters. Please adjust and try again.'
                : 'No Data'
            }
          />
        ) : loFilterLoading && activeOverview === TYPES.lo ? (
          <Loading style={{ height: '300px' }} />
        ) : (
          <rbd.DragDropContext onDragEnd={handleProjectDragEnd}>
            <rbd.Droppable droppableId="live-titles-projects">
              {(droppableProvided: any) => (
                <div
                  className="dbs-lto-project-rows"
                  ref={droppableProvided.innerRef}
                  {...droppableProvided.droppableProps}
                >
                  {projects?.map((project: any, index: number) => {
                    const draggableId = String(
                      project?.game_title ?? `project-${index}`,
                    );
                    return (
                      <rbd.Draggable
                        key={draggableId}
                        draggableId={draggableId}
                        index={index}
                        isDragDisabled={!isEditing}
                      >
                        {(draggableProvided: any, snapshot: any) => (
                          <div
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                            className={classnames('dbs-lto-project-row-draggable', {
                              'is-dragging': snapshot?.isDragging,
                            })}
                          >
                            <ProjectRow
                              project={project}
                              isEditing={isEditing}
                              activeOverview={activeOverview}
                              onIconClick={handleProjectIconClick}
                              dragHandleProps={draggableProvided.dragHandleProps}
                              selectedMonth={selectedMonth}
                            />
                          </div>
                        )}
                      </rbd.Draggable>
                    );
                  })}
                  {droppableProvided.placeholder}
                </div>
              )}
            </rbd.Droppable>
          </rbd.DragDropContext>
        )}
      </CardCommon>
    </>
  );
};

export default LiveTitlesOverview;
